import { Button, Flex, Pagination, Table, Tag } from "antd";
import React, { useState } from "react";
import { useSelector } from "react-redux";
import { LastUpdated } from "~/components/atom/LastUpdated.jsx";
import { $t, i18n } from "~/i18n.js";
import { api } from "~/lib/api.js";
import { _parseDateTime } from "~/lib/localize.js";
import { LedgerColumns } from "./LedgerColumns.jsx";
import { isLedgerLocked } from "./isLedgerLocked.js";

export function LedgerTable({
  ledgerEntries,
  loading,
  setOpenLedger,
  hasUsers,
  cancelDirectDebit,
  pagination,
  currentPageNumber,
  setCurrentPage,
  getLedgers,
  ledgerImportStatus,
  allowClearSelection = false,
  onUpdateLedger = null,
  readOnly,
  onEditDeclaration,
  onDeleteDeclaration,
}) {
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [loadingClearAmounts, setLoadingClearAmounts] = useState(false);
  const lockBeforeDate = useSelector((state) => state.persist?.club?.lock_before_date);

  const onSelectChange = (newSelectedRowKeys) => {
    setSelectedRowKeys(newSelectedRowKeys);
  };

  const rowSelection = allowClearSelection
    ? {
        selectedRowKeys,
        onChange: onSelectChange,
        getCheckboxProps: (record) => ({
          disabled: !!record.payment_status || !!record.declaration || isLedgerLocked(record, lockBeforeDate),
        }),
      }
    : null;

  const clearAmounts = async () => {
    setLoadingClearAmounts(true);

    for (const ledgerId of selectedRowKeys) {
      const ledger = ledgerEntries.find((l) => l.id === ledgerId);
      if (ledger && !ledger.payment_status && !ledger.declaration && !isLedgerLocked(ledger, lockBeforeDate)) {
        const payload = { amount: 0, id: ledger.id };

        try {
          await api._call_method(
            "PUT",
            `financial/ledgers/ledger.json`,
            payload,
          );

          // Update the ledger in the UI if callback provided
          if (onUpdateLedger) {
            onUpdateLedger({ ...ledger, amount: 0 });
          }
        } catch (error) {
          console.error(
            `Failed to clear amount for ledger ${ledger.id}:`,
            error,
          );
        }
      }
    }

    setSelectedRowKeys([]);
    setLoadingClearAmounts(false);
  };
  const columns = LedgerColumns({
    cancelDirectDebit: cancelDirectDebit,
    onEdit: setOpenLedger,
    hasUsers: hasUsers,
    ledgerImportStatus: ledgerImportStatus,
    readOnly: readOnly,
    lockBeforeDate: lockBeforeDate,
    onEditDeclaration: onEditDeclaration,
    onDeleteDeclaration: onDeleteDeclaration,
  });
  
  return (
    <section>
      <Table
        rowSelection={rowSelection}
        columns={columns}
        dataSource={ledgerEntries}
        loading={loading}
        rowKey="id"
        size="small"
        pagination={false}
        style={{ marginBottom: 24 }}
        expandable={{
          expandedRowRender: (record) => {
            const hasLines = record.lines && record.lines.length > 0;
            const hasRemark = record.remark?.length > 0;
            const hasCorrection =
              !record.is_custom &&
              !record.payment_status &&
              record.amount_calculated !== record.amount;
            const hasError = record.error?.length > 0;
            const hasImportDate =
              record.is_bank && record.amount_calculated === record.amount;
            const showAdminAdjustedDate =
              !hasCorrection &&
              !hasError &&
              !hasImportDate &&
              record.admin_updated_at;
            const showPendingInfo =
              record.is_directdebit &&
              (record.payment_status === "pending" ||
                record.payment_status === "scheduled");
            return (
              <div
                style={{ background: "#f6f6f6", padding: 6, marginLeft: 24 }}
              >
                {hasError && (
                  <div style={{ marginBottom: 8 }}>
                    <Tag color="red">{record.error}</Tag>
                  </div>
                )}
                {hasRemark && (
                  <p style={{ fontStyle: "italic", marginBottom: 2 }}>
                    {record.remark}
                  </p>
                )}
                {record.declaration?.file_url && (
                  <p style={{ marginBottom: 2 }}>
                    <a
                      href={record.declaration.file_url}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {record.declaration.filename}
                    </a>
                  </p>
                )}
                {hasLines && (
                  <ul
                    style={{
                      marginBottom: hasCorrection ? 12 : 0,
                      listStyle: "circle",
                      paddingLeft: 24,
                    }}
                  >
                    {record.lines?.map((line, i) => (
                      <li key={i} style={{ listStyleType: "circle" }}>
                        <span className="label" style={{ marginRight: 6 }}>
                          {line.description}
                        </span>
                        <span>
                          {i18n.parseLocaleCurrencyString(line.price)}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}

                {(hasCorrection || showAdminAdjustedDate) && (
                  <LastUpdated
                    date_updated={record.admin_updated_at}
                    updated_by={record.admin_updated_by?.name}
                  />
                )}

                {hasImportDate && (
                  <div>
                    {$t(
                      "Geïmporteerd door ${admin_updated_by} op ${admin_updated_at}",
                      {
                        admin_updated_by: record.admin_updated_by?.name || "-",
                        admin_updated_at:
                          _parseDateTime(record.admin_updated_at) || "-",
                      },
                    )}
                  </div>
                )}

                {showPendingInfo && (
                  <p className="small">
                    <b className="label">{$t("Incasso in afwachting")}</b>
                    {record.payment_status === "scheduled" &&
                      $t("Aangemaakt en wordt na 24 uur verwerkt.")}
                    {record.payment_status === "pending" &&
                      $t(
                        "Verwerkt maar wacht op goedkeuring van de bank. Dit kan enkele werkdagen duren.",
                      )}
                  </p>
                )}
              </div>
            );
          },
          rowExpandable: (record) =>
            record.error?.length > 0 ||
            record.remark?.length > 0 ||
            !!record.declaration?.file_url ||
            (record.amount_calculated !== record.amount &&
              !record.payment_status) ||
            record.is_custom ||
            (record.lines && record.lines.length > 0) ||
            (record.is_directdebit &&
              (record.payment_status === "pending" ||
                record.payment_status === "scheduled")) ||
            record.admin_updated_at,
        }}
      />

      {allowClearSelection && (
        <Flex align="center" gap="middle" style={{ marginBottom: 16 }}>
          <p>
            {$t("${n} geselecteerde mutatie(s)", { n: selectedRowKeys.length })}
          </p>

          <Button
            size="small"
            loading={loadingClearAmounts}
            onClick={clearAmounts}
            danger
          >
            {$t("Bedrag op 0 zetten")}
          </Button>
        </Flex>
      )}

      <Pagination
        simple={true}
        onChange={(newPage) => {
          setCurrentPage(newPage);
          getLedgers(newPage);
        }}
        showSizeChanger={false}
        pageSize={100}
        current={currentPageNumber}
        hideOnSinglePage={true}
        total={pagination?.count || ledgerEntries.length}
      />
    </section>
  );
}
